import { supabase } from '../lib/supabase';



/**
 * Profile Service - Centralized user profile management
 * Integrates profile data across applications, saved jobs, recommendations, and analytics
 */

class ProfileService {
  /**
   * Get complete user profile with all related data
   */
  async getCompleteProfile(userId) {
    try {
      const { data: profile, error } = await supabase?.from('user_profiles')?.select(`
          *,
          work_experience (*),
          education (*),
          skills (*),
          certifications (*),
          job_preferences (*),
          profile_views (count)
        `)?.eq('user_id', userId)?.single();

      if (error) throw error;

      // Transform to camelCase
      return this.transformProfileToCamelCase(profile);
    } catch (error) {
      console.error('Error fetching complete profile:', error);
      throw error;
    }
  }

  /**
   * Get profile summary for display in applications and saved jobs
   */
  async getProfileSummary(userId) {
    try {
      const { data: profile, error } = await supabase?.from('user_profiles')?.select(`
          user_id,
          full_name,
          email,
          phone,
          profile_picture_url,
          headline,
          location,
          resume_url,
          linkedin_url,
          portfolio_url,
          github_url
        `)?.eq('user_id', userId)?.single();

      if (error) throw error;

      return this.transformProfileToCamelCase(profile);
    } catch (error) {
      console.error('Error fetching profile summary:', error);
      throw error;
    }
  }

  /**
   * Update user profile
   */
  async updateProfile(userId, profileData) {
    try {
      const snakeCaseData = this.transformProfileToSnakeCase(profileData);

      const { data, error } = await supabase?.from('user_profiles')?.update(snakeCaseData)?.eq('user_id', userId)?.select()?.single();

      if (error) throw error;

      return this.transformProfileToCamelCase(data);
    } catch (error) {
      console.error('Error updating profile:', error);
      throw error;
    }
  }

  /**
   * Get profile with application statistics
   */
  async getProfileWithApplicationStats(userId) {
    try {
      const [profile, stats] = await Promise.all([
        this.getProfileSummary(userId),
        this.getApplicationStatistics(userId)
      ]);

      return {
        ...profile,
        applicationStats: stats
      };
    } catch (error) {
      console.error('Error fetching profile with stats:', error);
      throw error;
    }
  }

  /**
   * Get application statistics for profile
   */
  async getApplicationStatistics(userId) {
    try {
      const { data, error } = await supabase?.from('job_applications')?.select('status')?.eq('user_id', userId);

      if (error) throw error;

      const stats = {
        total: data?.length,
        pending: data?.filter(app => app?.status === 'pending')?.length,
        reviewing: data?.filter(app => app?.status === 'reviewing')?.length,
        interviewing: data?.filter(app => app?.status === 'interviewing')?.length,
        offered: data?.filter(app => app?.status === 'offered')?.length,
        rejected: data?.filter(app => app?.status === 'rejected')?.length,
        accepted: data?.filter(app => app?.status === 'accepted')?.length,
        withdrawn: data?.filter(app => app?.status === 'withdrawn')?.length
      };

      return stats;
    } catch (error) {
      console.error('Error fetching application statistics:', error);
      throw error;
    }
  }

  /**
   * Get work experience for profile
   */
  async getWorkExperience(userId) {
    try {
      const { data, error } = await supabase?.from('work_experience')?.select('*')?.eq('user_id', userId)?.order('start_date', { ascending: false });

      if (error) throw error;

      return data?.map(exp => this.transformToCamelCase(exp));
    } catch (error) {
      console.error('Error fetching work experience:', error);
      throw error;
    }
  }

  /**
   * Get education for profile
   */
  async getEducation(userId) {
    try {
      const { data, error } = await supabase?.from('education')?.select('*')?.eq('user_id', userId)?.order('start_date', { ascending: false });

      if (error) throw error;

      return data?.map(edu => this.transformToCamelCase(edu));
    } catch (error) {
      console.error('Error fetching education:', error);
      throw error;
    }
  }

  /**
   * Get skills for profile
   */
  async getSkills(userId) {
    try {
      const { data, error } = await supabase?.from('skills')?.select('*')?.eq('user_id', userId)?.order('proficiency_level', { ascending: false });

      if (error) throw error;

      return data?.map(skill => this.transformToCamelCase(skill));
    } catch (error) {
      console.error('Error fetching skills:', error);
      throw error;
    }
  }

  /**
   * Get certifications for profile
   */
  async getCertifications(userId) {
    try {
      const { data, error } = await supabase?.from('certifications')?.select('*')?.eq('user_id', userId)?.order('issue_date', { ascending: false });

      if (error) throw error;

      return data?.map(cert => this.transformToCamelCase(cert));
    } catch (error) {
      console.error('Error fetching certifications:', error);
      throw error;
    }
  }

  /**
   * Get job preferences for profile
   */
  async getJobPreferences(userId) {
    try {
      const { data, error } = await supabase?.from('job_preferences')?.select('*')?.eq('user_id', userId)?.single();

      if (error) throw error;

      return this.transformToCamelCase(data);
    } catch (error) {
      console.error('Error fetching job preferences:', error);
      throw error;
    }
  }

  /**
   * Add work experience
   */
  async addWorkExperience(userId, experienceData) {
    try {
      const snakeCaseData = this.transformToSnakeCase({
        ...experienceData,
        userId
      });

      const { data, error } = await supabase?.from('work_experience')?.insert(snakeCaseData)?.select()?.single();

      if (error) throw error;

      return this.transformToCamelCase(data);
    } catch (error) {
      console.error('Error adding work experience:', error);
      throw error;
    }
  }

  /**
   * Update work experience
   */
  async updateWorkExperience(experienceId, experienceData) {
    try {
      const snakeCaseData = this.transformToSnakeCase(experienceData);

      const { data, error } = await supabase?.from('work_experience')?.update(snakeCaseData)?.eq('id', experienceId)?.select()?.single();

      if (error) throw error;

      return this.transformToCamelCase(data);
    } catch (error) {
      console.error('Error updating work experience:', error);
      throw error;
    }
  }

  /**
   * Delete work experience
   */
  async deleteWorkExperience(experienceId) {
    try {
      const { error } = await supabase?.from('work_experience')?.delete()?.eq('id', experienceId);

      if (error) throw error;

      return { success: true };
    } catch (error) {
      console.error('Error deleting work experience:', error);
      throw error;
    }
  }

  /**
   * Add education
   */
  async addEducation(userId, educationData) {
    try {
      const snakeCaseData = this.transformToSnakeCase({
        ...educationData,
        userId
      });

      const { data, error } = await supabase?.from('education')?.insert(snakeCaseData)?.select()?.single();

      if (error) throw error;

      return this.transformToCamelCase(data);
    } catch (error) {
      console.error('Error adding education:', error);
      throw error;
    }
  }

  /**
   * Update education
   */
  async updateEducation(educationId, educationData) {
    try {
      const snakeCaseData = this.transformToSnakeCase(educationData);

      const { data, error } = await supabase?.from('education')?.update(snakeCaseData)?.eq('id', educationId)?.select()?.single();

      if (error) throw error;

      return this.transformToCamelCase(data);
    } catch (error) {
      console.error('Error updating education:', error);
      throw error;
    }
  }

  /**
   * Delete education
   */
  async deleteEducation(educationId) {
    try {
      const { error } = await supabase?.from('education')?.delete()?.eq('id', educationId);

      if (error) throw error;

      return { success: true };
    } catch (error) {
      console.error('Error deleting education:', error);
      throw error;
    }
  }

  /**
   * Add skill
   */
  async addSkill(userId, skillData) {
    try {
      const snakeCaseData = this.transformToSnakeCase({
        ...skillData,
        userId
      });

      const { data, error } = await supabase?.from('skills')?.insert(snakeCaseData)?.select()?.single();

      if (error) throw error;

      return this.transformToCamelCase(data);
    } catch (error) {
      console.error('Error adding skill:', error);
      throw error;
    }
  }

  /**
   * Update skill
   */
  async updateSkill(skillId, skillData) {
    try {
      const snakeCaseData = this.transformToSnakeCase(skillData);

      const { data, error } = await supabase?.from('skills')?.update(snakeCaseData)?.eq('id', skillId)?.select()?.single();

      if (error) throw error;

      return this.transformToCamelCase(data);
    } catch (error) {
      console.error('Error updating skill:', error);
      throw error;
    }
  }

  /**
   * Delete skill
   */
  async deleteSkill(skillId) {
    try {
      const { error } = await supabase?.from('skills')?.delete()?.eq('id', skillId);

      if (error) throw error;

      return { success: true };
    } catch (error) {
      console.error('Error deleting skill:', error);
      throw error;
    }
  }

  /**
   * Add certification
   */
  async addCertification(userId, certificationData) {
    try {
      const snakeCaseData = this.transformToSnakeCase({
        ...certificationData,
        userId
      });

      const { data, error } = await supabase?.from('certifications')?.insert(snakeCaseData)?.select()?.single();

      if (error) throw error;

      return this.transformToCamelCase(data);
    } catch (error) {
      console.error('Error adding certification:', error);
      throw error;
    }
  }

  /**
   * Update certification
   */
  async updateCertification(certificationId, certificationData) {
    try {
      const snakeCaseData = this.transformToSnakeCase(certificationData);

      const { data, error } = await supabase?.from('certifications')?.update(snakeCaseData)?.eq('id', certificationId)?.select()?.single();

      if (error) throw error;

      return this.transformToCamelCase(data);
    } catch (error) {
      console.error('Error updating certification:', error);
      throw error;
    }
  }

  /**
   * Delete certification
   */
  async deleteCertification(certificationId) {
    try {
      const { error } = await supabase?.from('certifications')?.delete()?.eq('id', certificationId);

      if (error) throw error;

      return { success: true };
    } catch (error) {
      console.error('Error deleting certification:', error);
      throw error;
    }
  }

  /**
   * Update job preferences
   */
  async updateJobPreferences(userId, preferencesData) {
    try {
      const snakeCaseData = this.transformToSnakeCase(preferencesData);

      // Check if preferences exist
      const { data: existing } = await supabase?.from('job_preferences')?.select('id')?.eq('user_id', userId)?.single();

      let result;

      if (existing) {
        // Update existing preferences
        result = await supabase?.from('job_preferences')?.update(snakeCaseData)?.eq('user_id', userId)?.select()?.single();
      } else {
        // Insert new preferences
        result = await supabase?.from('job_preferences')?.insert({ ...snakeCaseData, user_id: userId })?.select()?.single();
      }

      if (result?.error) throw result?.error;

      return this.transformToCamelCase(result?.data);
    } catch (error) {
      console.error('Error updating job preferences:', error);
      throw error;
    }
  }

  /**
   * Get profile completeness score
   */
  async getProfileCompleteness(userId) {
    try {
      const [
        profile,
        workExperience,
        education,
        skills,
        certifications,
        preferences
      ] = await Promise.all([
        this.getProfileSummary(userId),
        this.getWorkExperience(userId),
        this.getEducation(userId),
        this.getSkills(userId),
        this.getCertifications(userId),
        this.getJobPreferences(userId)?.catch(() => null)
      ]);

      const sections = {
        basicInfo: this.calculateBasicInfoScore(profile),
        workExperience: workExperience?.length > 0 ? 100 : 0,
        education: education?.length > 0 ? 100 : 0,
        skills: skills?.length >= 5 ? 100 : (skills?.length * 20),
        certifications: certifications?.length > 0 ? 100 : 0,
        preferences: preferences ? 100 : 0
      };

      const totalScore = Math.round(
        Object.values(sections)?.reduce((sum, score) => sum + score, 0) / Object.keys(sections)?.length
      );

      return {
        totalScore,
        sections,
        suggestions: this.generateCompletionSuggestions(sections)
      };
    } catch (error) {
      console.error('Error calculating profile completeness:', error);
      throw error;
    }
  }

  /**
   * Calculate basic info completion score
   */
  calculateBasicInfoScore(profile) {
    const fields = [
      'fullName',
      'email',
      'phone',
      'headline',
      'location',
      'resumeUrl',
      'linkedinUrl'
    ];

    const completed = fields?.filter(field => profile?.[field])?.length;
    return Math.round((completed / fields?.length) * 100);
  }

  /**
   * Generate profile completion suggestions
   */
  generateCompletionSuggestions(sections) {
    const suggestions = [];

    if (sections?.basicInfo < 100) {
      suggestions?.push('Complete your basic profile information');
    }
    if (sections?.workExperience === 0) {
      suggestions?.push('Add your work experience');
    }
    if (sections?.education === 0) {
      suggestions?.push('Add your education history');
    }
    if (sections?.skills < 100) {
      suggestions?.push('Add at least 5 skills to your profile');
    }
    if (sections?.certifications === 0) {
      suggestions?.push('Add any certifications you have');
    }
    if (sections?.preferences === 0) {
      suggestions?.push('Set your job preferences');
    }

    return suggestions;
  }

  /**
   * Track profile view
   */
  async trackProfileView(viewerId, profileUserId, source = 'direct') {
    try {
      // Don't track self-views
      if (viewerId === profileUserId) {
        return null;
      }

      const { data, error } = await supabase?.from('profile_views')?.insert({
          viewer_id: viewerId,
          profile_user_id: profileUserId,
          source,
          viewed_at: new Date()?.toISOString()
        })?.select()?.single();

      if (error) throw error;

      return this.transformToCamelCase(data);
    } catch (error) {
      console.error('Error tracking profile view:', error);
      throw error;
    }
  }

  /**
   * Get profile views analytics
   */
  async getProfileViewsAnalytics(userId, days = 30) {
    try {
      const startDate = new Date();
      startDate?.setDate(startDate?.getDate() - days);

      const { data, error } = await supabase?.from('profile_views')?.select('*')?.eq('profile_user_id', userId)?.gte('viewed_at', startDate?.toISOString())?.order('viewed_at', { ascending: false });

      if (error) throw error;

      const views = data?.map(view => this.transformToCamelCase(view));

      return {
        total: views?.length,
        bySource: this.groupViewsBySource(views),
        recent: views?.slice(0, 10),
        trend: this.calculateViewTrend(views, days)
      };
    } catch (error) {
      console.error('Error fetching profile views analytics:', error);
      throw error;
    }
  }

  /**
   * Group views by source
   */
  groupViewsBySource(views) {
    return views?.reduce((acc, view) => {
      acc[view.source] = (acc?.[view?.source] || 0) + 1;
      return acc;
    }, {});
  }

  /**
   * Calculate view trend
   */
  calculateViewTrend(views, days) {
    const dailyViews = {};
    
    for (let i = 0; i < days; i++) {
      const date = new Date();
      date?.setDate(date?.getDate() - i);
      const dateKey = date?.toISOString()?.split('T')?.[0];
      dailyViews[dateKey] = 0;
    }

    views?.forEach(view => {
      const dateKey = view?.viewedAt?.split('T')?.[0];
      if (dailyViews?.[dateKey] !== undefined) {
        dailyViews[dateKey]++;
      }
    });

    return Object.entries(dailyViews)?.map(([date, count]) => ({ date, count }))?.sort((a, b) => a?.date?.localeCompare(b?.date));
  }

  /**
   * Transform profile to camelCase
   */
  transformProfileToCamelCase(profile) {
    if (!profile) return null;

    return {
      userId: profile?.user_id,
      fullName: profile?.full_name,
      email: profile?.email,
      phone: profile?.phone,
      profilePictureUrl: profile?.profile_picture_url,
      headline: profile?.headline,
      bio: profile?.bio,
      location: profile?.location,
      city: profile?.city,
      state: profile?.state,
      country: profile?.country,
      zipCode: profile?.zip_code,
      resumeUrl: profile?.resume_url,
      coverLetterUrl: profile?.cover_letter_url,
      linkedinUrl: profile?.linkedin_url,
      portfolioUrl: profile?.portfolio_url,
      githubUrl: profile?.github_url,
      twitterUrl: profile?.twitter_url,
      websiteUrl: profile?.website_url,
      yearsOfExperience: profile?.years_of_experience,
      currentJobTitle: profile?.current_job_title,
      currentCompany: profile?.current_company,
      expectedSalary: profile?.expected_salary,
      availableForWork: profile?.available_for_work,
      openToRemote: profile?.open_to_remote,
      openToRelocation: profile?.open_to_relocation,
      visaStatus: profile?.visa_status,
      profileVisibility: profile?.profile_visibility,
      emailNotifications: profile?.email_notifications,
      smsNotifications: profile?.sms_notifications,
      createdAt: profile?.created_at,
      updatedAt: profile?.updated_at,
      workExperience: profile?.work_experience,
      education: profile?.education,
      skills: profile?.skills,
      certifications: profile?.certifications,
      jobPreferences: profile?.job_preferences,
      profileViews: profile?.profile_views
    };
  }

  /**
   * Transform profile to snake_case
   */
  transformProfileToSnakeCase(profile) {
    if (!profile) return null;

    const snakeCase = {};

    if (profile?.fullName !== undefined) snakeCase.full_name = profile?.fullName;
    if (profile?.email !== undefined) snakeCase.email = profile?.email;
    if (profile?.phone !== undefined) snakeCase.phone = profile?.phone;
    if (profile?.profilePictureUrl !== undefined) snakeCase.profile_picture_url = profile?.profilePictureUrl;
    if (profile?.headline !== undefined) snakeCase.headline = profile?.headline;
    if (profile?.bio !== undefined) snakeCase.bio = profile?.bio;
    if (profile?.location !== undefined) snakeCase.location = profile?.location;
    if (profile?.city !== undefined) snakeCase.city = profile?.city;
    if (profile?.state !== undefined) snakeCase.state = profile?.state;
    if (profile?.country !== undefined) snakeCase.country = profile?.country;
    if (profile?.zipCode !== undefined) snakeCase.zip_code = profile?.zipCode;
    if (profile?.resumeUrl !== undefined) snakeCase.resume_url = profile?.resumeUrl;
    if (profile?.coverLetterUrl !== undefined) snakeCase.cover_letter_url = profile?.coverLetterUrl;
    if (profile?.linkedinUrl !== undefined) snakeCase.linkedin_url = profile?.linkedinUrl;
    if (profile?.portfolioUrl !== undefined) snakeCase.portfolio_url = profile?.portfolioUrl;
    if (profile?.githubUrl !== undefined) snakeCase.github_url = profile?.githubUrl;
    if (profile?.twitterUrl !== undefined) snakeCase.twitter_url = profile?.twitterUrl;
    if (profile?.websiteUrl !== undefined) snakeCase.website_url = profile?.websiteUrl;
    if (profile?.yearsOfExperience !== undefined) snakeCase.years_of_experience = profile?.yearsOfExperience;
    if (profile?.currentJobTitle !== undefined) snakeCase.current_job_title = profile?.currentJobTitle;
    if (profile?.currentCompany !== undefined) snakeCase.current_company = profile?.currentCompany;
    if (profile?.expectedSalary !== undefined) snakeCase.expected_salary = profile?.expectedSalary;
    if (profile?.availableForWork !== undefined) snakeCase.available_for_work = profile?.availableForWork;
    if (profile?.openToRemote !== undefined) snakeCase.open_to_remote = profile?.openToRemote;
    if (profile?.openToRelocation !== undefined) snakeCase.open_to_relocation = profile?.openToRelocation;
    if (profile?.visaStatus !== undefined) snakeCase.visa_status = profile?.visaStatus;
    if (profile?.profileVisibility !== undefined) snakeCase.profile_visibility = profile?.profileVisibility;
    if (profile?.emailNotifications !== undefined) snakeCase.email_notifications = profile?.emailNotifications;
    if (profile?.smsNotifications !== undefined) snakeCase.sms_notifications = profile?.smsNotifications;

    return snakeCase;
  }

  /**
   * Generic transform to camelCase
   */
  transformToCamelCase(obj) {
    if (!obj) return null;

    const camelCase = {};
    Object.keys(obj)?.forEach(key => {
      const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
      camelCase[camelKey] = obj?.[key];
    });

    return camelCase;
  }

  /**
   * Generic transform to snake_case
   */
  transformToSnakeCase(obj) {
    if (!obj) return null;

    const snakeCase = {};
    Object.keys(obj)?.forEach(key => {
      const snakeKey = key?.replace(/([A-Z])/g, '_$1')?.toLowerCase();
      snakeCase[snakeKey] = obj?.[key];
    });

    return snakeCase;
  }
}

export default new ProfileService();
function profileService(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: profileService is not implemented yet.', args);
  return null;
}

export { profileService };